import {
  Button,
  cn,
  Label,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@beweegbaas/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, stripSearchParams, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { Fragment } from "react";
import { z } from "zod/v4";
import { Page } from "#/components/page";
import { useTableNavigation } from "#/hooks/use-table-navigation";
import { BeweegresultaatField } from "#/modules/les.docent-gestuurd/components/field.beweegresultaat";
import { BeweegresultaatOpmerkingenField } from "#/modules/les.docent-gestuurd/components/field.beweegresultaat-opmerkingen";
import { SociaalEmotioneelOpmerkingenField } from "#/modules/les.docent-gestuurd/components/field.sociaalemotioneel-opmerkingen";
import { SociaalEmotioneelField } from "#/modules/les.docent-gestuurd/components/field.sociaalemotioneel-resultaat";
import { PreviousScoreIndicator } from "#/modules/les.docent-gestuurd/components/previous-score-indicator";
import { toggleAvatars } from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { useDocentLes } from "#/modules/les.docent-gestuurd/use-docent-les";
import { BeweegactiviteitDoelenSummary } from "#/modules/les.shared/components/beweegactiviteit-doelen-summary";
import { SociaalEmotioneelThemasSummary } from "#/modules/les.shared/components/sociaalemotioneel-themas-summary";
import { baseColorVariants, getGroupColor } from "#/modules/les.shared/group-colors";
import { getTeacherLesResultaten } from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { getInnerLeerjaarNumberTranslation } from "#/modules/util.i18n/enum-mapping";
import { OverachievedIconButton } from "#/modules/util.score-status/components/score-status";
import { nuqsBoolean } from "#/modules/util.pagination/search.nuqs";

const scoresSearchSchema = z.object({
  hideScoreColors: nuqsBoolean(false),
});

/** Defaults stripped from the URL by `stripSearchParams`. */
const SCORES_SEARCH_DEFAULTS = { hideScoreColors: false };

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher/scores",
)({
  validateSearch: scoresSearchSchema,
  search: { middlewares: [stripSearchParams(SCORES_SEARCH_DEFAULTS)] },
  loader: ({ params }) => getTeacherLesResultaten({ data: { lesId: params.lesson } }),
  component: TeacherLessonScores,
});

function TeacherLessonScores() {
  const { resultaten } = Route.useLoaderData();
  const { locale, schoolyear, school, lesson } = Route.useParams();
  const { lesson: les } = useDocentLes();
  const router = useRouter();
  const toggleAvatarsFn = useServerFn(toggleAvatars);
  const toggleAvatarsMutation = useMutation({
    // The endpoint has toggle semantics; the scope serializes rapid clicks so
    // concurrent toggles can't interleave server-side.
    scope: { id: `toggle-avatars-${lesson}` },
    mutationFn: (_hide: boolean) => toggleAvatarsFn({ data: { lesId: lesson } }),
    // Returned promise keeps isPending true until loaders re-ran, so the
    // optimistic value below never flickers back to the stale loader value.
    onSuccess: () => router.invalidate({ sync: true }),
  });
  const hideAvatars = toggleAvatarsMutation.isPending
    ? (toggleAvatarsMutation.variables ?? !les.avatarsEnabled)
    : !les.avatarsEnabled;

  // Enable table navigation for score inputs
  useTableNavigation({
    selector: "input[inputmode='numeric']",
  });

  const navigate = Route.useNavigate();
  const { hideScoreColors } = Route.useSearch();
  const setHideScoreColors = (checked: boolean) =>
    navigate({
      search: (prev) => ({ ...prev, hideScoreColors: checked }),
      replace: true,
      resetScroll: false,
    });

  return (
    <>
      <Page.Container className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <Switch
            id="hide-score-colors"
            checked={hideScoreColors}
            onCheckedChange={(checked) => setHideScoreColors(checked)}
            aria-describedby="hide-score-colors-description"
          />
          <Label
            htmlFor="hide-score-colors"
            id="hide-score-colors-description"
            className="cursor-pointer text-sm font-medium"
          >
            Scorekleuren verbergen
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="hide-avatars"
            checked={hideAvatars}
            onCheckedChange={(checked) => toggleAvatarsMutation.mutate(checked)}
            aria-describedby="hide-avatars-description"
          />
          <Label
            htmlFor="hide-avatars"
            id="hide-avatars-description"
            className="cursor-pointer text-sm font-medium"
          >
            Avatars verbergen
          </Label>
        </div>
      </Page.Container>

      <div className="mx-auto w-full max-w-(--container-max-width)">
        {/* Small size (h-10/px-2 cells) + container-gutter edge cells, inlined from the legacy table variants. */}
        <Table
          aria-label="Student Goals"
          className="[&_td]:h-10 [&_td]:px-2 [&_th]:px-2 [&_tr>*:first-child]:pl-(--container-gutter)! [&_tr>*:last-child]:pr-(--container-gutter)!"
        >
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10 text-nowrap" />
              <TableHead className="pl-0!">Leerling</TableHead>
              {resultaten?.beweegactiviteiten.map((beweegactiviteit) => {
                return (
                  <TableHead key={beweegactiviteit.id} className="min-w-[20ch] text-nowrap">
                    <div className={"flex items-center gap-2"}>
                      {beweegactiviteit.name}
                      <Popover>
                        <PopoverTrigger
                          render={
                            <Button
                              variant={"ghost"}
                              aria-label="Help"
                              size={"icon"}
                              className={"-my-3"}
                              nativeButton={true}
                            >
                              <IconMaterialSymbolsHelpOutline className="h-4 w-4" />
                            </Button>
                          }
                        />
                        <PopoverContent side="bottom" align="start" className="max-w-[250px]">
                          <BeweegactiviteitDoelenSummary
                            className="text-sm"
                            beweegactiviteit={beweegactiviteit}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </TableHead>
                );
              })}

              {resultaten?.sociaalEmotioneelThemas.map((sociaalEmotioneelThema) => {
                return (
                  <TableHead key={sociaalEmotioneelThema.id} className="text-nowrap">
                    <div className={"flex items-center gap-2"}>
                      {sociaalEmotioneelThema.name}
                      <Popover>
                        <PopoverTrigger
                          render={
                            <Button
                              variant={"ghost"}
                              aria-label="Help"
                              size={"icon"}
                              className="-my-3"
                              nativeButton={true}
                            >
                              <IconMaterialSymbolsHelpOutline className="h-4 w-4" />
                            </Button>
                          }
                        />
                        <PopoverContent side="bottom" align="start" className="max-w-[250px]">
                          <SociaalEmotioneelThemasSummary
                            className="text-sm"
                            sociaalEmotioneelThema={sociaalEmotioneelThema}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {resultaten?.groupedLeerlingen.map((leerling, index) => {
              return (
                <Fragment key={leerling.groep}>
                  {index > 0 && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell className="h-10 w-10" colSpan={8}></TableCell>
                    </TableRow>
                  )}
                  {leerling.leerlingen.map((leerling) => (
                    <TableRow key={leerling.leerlingId}>
                      <TableCell data-table-sticky-left="" className="sticky left-0 z-10 w-10">
                        <span
                          className={cn(
                            `inline-flex size-5 items-center justify-center rounded
                            font-bold`,
                            baseColorVariants({
                              color: getGroupColor(leerling.groep),
                              textColor: "dark",
                            }),
                          )}
                        >
                          {leerling.groep === -1 ? (
                            <IconTablerX className="size-4" />
                          ) : (
                            leerling.groep
                          )}
                        </span>
                      </TableCell>
                      <TableCell
                        data-table-sticky-left="edge"
                        className="sticky left-[calc(var(--container-gutter)+2rem)] z-10 pl-0!"
                      >
                        <Link
                          variant="secondary"
                          underline={true}
                          to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
                          params={{ locale, schoolyear, school, id: leerling.leerlingId }}
                        >
                          {leerling.leerlingNaam}
                        </Link>
                        {/* (klasnaam - leerjaar): klasnaam only when the les spans multiple
                            klassen, leerjaar only when the klassen combine leerjaren. */}
                        {((leerling.showKlas && leerling.klasNaam) ||
                          leerling.showLeerlingLeerjaar) && (
                          <span className="text-muted-foreground ml-1">
                            {`(${[
                              leerling.showKlas ? leerling.klasNaam : null,
                              leerling.showLeerlingLeerjaar
                                ? getInnerLeerjaarNumberTranslation(leerling.leerlingLeerjaar)
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" - ")})`}
                          </span>
                        )}
                      </TableCell>
                      {resultaten?.beweegactiviteiten.map((beweegactiviteit) => {
                        const beweegresultaat = leerling.beweegresultaten.find(
                          (r) => r.beweegactiviteitId === beweegactiviteit.id,
                        );
                        return (
                          <TableCell key={beweegactiviteit.id}>
                            <div className="flex items-center gap-2">
                              <BeweegresultaatField
                                beweegactiviteit={beweegactiviteit}
                                beweegresultaat={beweegresultaat}
                                niveau={beweegresultaat?.behaaldNiveau}
                                waarde={beweegresultaat?.behaaldWaarde}
                                lesId={lesson}
                                leerlingId={leerling.leerlingId}
                                beweegactiviteitId={beweegactiviteit.id!}
                                className={cn(hideScoreColors && "grayscale")}
                              />
                              <BeweegresultaatOpmerkingenField
                                lesId={lesson}
                                leerlingId={leerling.leerlingId}
                                beweegactiviteitId={beweegactiviteit.id!}
                                value={beweegresultaat?.opmerking}
                              />
                              <PreviousScoreIndicator
                                beweegresultaat={beweegresultaat}
                                beweegactiviteit={beweegactiviteit}
                              />
                              {beweegresultaat?.isOverachieved && <OverachievedIconButton />}
                            </div>
                          </TableCell>
                        );
                      })}

                      {resultaten?.sociaalEmotioneelThemas.map((sociaalEmotioneelThemaDetail) => {
                        const sociaalEmotioneelResultaat =
                          leerling.sociaalEmotioneelResultaten.find(
                            (r) =>
                              r.sociaalEmotioneelThemaId ===
                              String(sociaalEmotioneelThemaDetail.id),
                          );
                        return (
                          <TableCell className="pr-4!" key={sociaalEmotioneelThemaDetail.id}>
                            <div className="flex items-center gap-2">
                              <SociaalEmotioneelField
                                sociaalEmotioneelThemaDetail={sociaalEmotioneelThemaDetail}
                                lesId={lesson}
                                leerlingId={leerling.leerlingId}
                                sociaalEmotioneelThemaId={String(sociaalEmotioneelThemaDetail.id)}
                                sociaalEmotioneelDoelIds={
                                  sociaalEmotioneelResultaat?.behaaldeSociaalEmotioneelDoelen || []
                                }
                              />
                              <SociaalEmotioneelOpmerkingenField
                                lesId={lesson}
                                leerlingId={leerling.leerlingId}
                                sociaalEmotioneelThemaId={String(sociaalEmotioneelThemaDetail.id)}
                                value={sociaalEmotioneelResultaat?.opmerking}
                              />
                              {sociaalEmotioneelResultaat?.isOverachieved && (
                                <OverachievedIconButton />
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
